import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import dayjs from 'dayjs';
import { Observable, catchError, filter, map, of, startWith, switchMap } from 'rxjs';
import {
  TargetedDepartmentCategoriesCardComponent,
  TargetedDepartmentCategoryOption,
  getToAnswerDepartmentCategoryIds,
  setTargetedDepartmentCategorySelection,
} from 'src/app/components/main/shared/targeted-department-categories-card/targeted-department-categories-card.component';
import { RoleSlugEnum } from 'src/app/enums/role-slug.enum';
import { DepartmentCategory } from 'src/app/models/department_category.model';
import {
  TargetedRolesCardComponent,
  getToAnswerRoleIds,
  setTargetedRoleSelection,
} from 'src/app/components/main/shared/targeted-roles-card/targeted-roles-card.component';
import { PendingChangesSaveStatusComponent } from 'src/app/components/main/shared/pending-changes-save-status/pending-changes-save-status.component';
import {
  AddSurveySectionDialogComponent,
  getSurveySectionType,
} from 'src/app/components/main/employees/rrhh/survey-edit/add-survey-section-dialog/add-survey-section-dialog.component';
import { SurveyStatusEnum, SurveyStatusEnumLabels } from 'src/app/enums/survey-status.enum';
import {
  SurveyVariableConditionFrequencyEnum,
  SurveyVariableConditionFrequencyEnumLabels,
} from 'src/app/enums/survey-variable-condition-frequency.enum';
import { SurveyVariableConditionSectionTypeEnum } from 'src/app/enums/survey-variable-condition-section-type.enum';
import { ComponentCanDeactivate, PendingChangesGuard } from 'src/app/guards/pending-changes.guard';
import { Role } from 'src/app/models/role.model';
import { Survey, SurveyFilter, UpsertSurveyBody, SurveyVariableConditionPayload, SurveySectionDraft } from 'src/app/models/survey.model';
import { SurveyVariableConditionFilterDirectionEnum } from 'src/app/enums/survey-variable-condition-filter-direction.enum';
import {
  SurveyVariableConditionSection,
  SurveyVariableConditionSectionPayload,
} from 'src/app/models/survey-variable-condition-section.model';
import { SurveyVariableCondition } from 'src/app/models/survey-variable-condition.model';
import { ApiService } from 'src/app/services';
import { createPendingChangesSaveStatus } from 'src/app/utils/pending-changes-save-status';
import { dateAfterStartValidator } from 'src/app/validators/date-after-start.validator';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-survey-edit',
  templateUrl: './survey-edit.component.html',
  styleUrl: './survey-edit.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    RouterLink,
    TargetedRolesCardComponent,
    PendingChangesSaveStatusComponent,
    TargetedDepartmentCategoriesCardComponent,
    DragDropModule,
    MatTooltipModule,
  ],
})
export class SurveyEditComponent implements OnInit, ComponentCanDeactivate {
  private readonly api_service = inject(ApiService);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly activated_route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly form_builder = inject(FormBuilder);
  private readonly mat_dialog = inject(MatDialog);
  private readonly snack_bar = inject(MatSnackBar);
  private readonly pending_changes_guard = inject(PendingChangesGuard);

  readonly loading = signal(false);
  readonly roles = signal<Role[]>([]);
  readonly survey = signal<Survey | null>(null);
  readonly sections = signal<SurveySectionDraft[]>([]);
  readonly roles_error = signal<string | null>(null);
  readonly department_categories_error = signal<string | null>(null);
  protected readonly pending_changes_save_status = createPendingChangesSaveStatus();

  readonly is_create_mode = toSignal(this.activated_route.data.pipe(map(route_data => route_data['mode'] === 'create')), {
    initialValue: false,
  });

  readonly survey_id = toSignal(
    this.activated_route.paramMap.pipe(
      map(param_map => {
        const raw_id = param_map.get('id');

        if (!raw_id) {
          return null;
        }

        const parsed_id = Number.parseInt(raw_id, 10);

        return Number.isNaN(parsed_id) ? null : parsed_id;
      })
    ),
    { initialValue: null as number | null }
  );

  readonly form = this.form_builder.nonNullable.group({
    title: ['', [Validators.required, Validators.maxLength(255)]],
    status: [SurveyStatusEnum.Draft as SurveyStatusEnum, Validators.required],
    frequency: [SurveyVariableConditionFrequencyEnum.Quarterly as SurveyVariableConditionFrequencyEnum, Validators.required],
    start_date: ['', Validators.required],
    end_date: ['', [Validators.required, dateAfterStartValidator('start_date')]],
    postponable_days: [0, [Validators.required, Validators.min(0)]],
    role_ids: [[] as number[], [minSelectedValidator(1)]],
    department_category_ids: [[] as number[]],
  });

  private readonly selected_role_ids = toSignal(
    this.form.controls.role_ids.valueChanges.pipe(startWith(this.form.controls.role_ids.value)),
    { initialValue: this.form.controls.role_ids.value }
  );

  readonly department_categories = toSignal(
    this.api_service.getDepartmentCategories({ per_page: 500, order_by: 'name:asc' }).pipe(
      map((response): DepartmentCategory[] => (Array.isArray(response) ? response : (response.data ?? []))),
      catchError(() => of<DepartmentCategory[]>([]))
    ),
    { initialValue: [] as DepartmentCategory[] }
  );

  readonly worker_role_selected = computed((): boolean =>
    this.roles().some(role => role.slug === RoleSlugEnum.WORKER && this.selected_role_ids().includes(role.id))
  );

  readonly department_category_options = computed((): TargetedDepartmentCategoryOption[] =>
    this.department_categories().map(department_category => ({
      id: department_category.id,
      name: department_category.name,
    }))
  );

  readonly status_options = (Object.values(SurveyStatusEnum) as SurveyStatusEnum[]).map(value => ({
    value,
    label: SurveyStatusEnumLabels[value],
  }));

  readonly frequency_options = Object.values(SurveyVariableConditionFrequencyEnum).map(value => ({
    value,
    label: SurveyVariableConditionFrequencyEnumLabels[value],
  }));

  readonly title_draft = toSignal(this.form.controls.title.valueChanges.pipe(startWith(this.form.controls.title.value)), {
    initialValue: '',
  });

  readonly breadcrumb_survey_title = computed(() => {
    const draft = this.title_draft().trim();

    if (draft !== '') {
      return draft;
    }

    if (this.is_create_mode()) {
      return 'Crear';
    }

    return this.survey()?.title ?? null;
  });

  private readonly editor_route = computed(() => ({
    is_create: this.is_create_mode(),
    survey_id: this.survey_id(),
  }));

  private readonly editor_page_load = toSignal<SurveyEditorPageLoad | null>(
    toObservable(this.editor_route).pipe(
      switchMap(
        ({ is_create, survey_id }): Observable<SurveyEditorPageLoad> =>
          this.api_service.getRoles().pipe(
            switchMap(roles => {
              if (is_create) {
                return of<SurveyEditorPageLoad>({ status: 'create', roles });
              }

              if (survey_id === null) {
                return of<SurveyEditorPageLoad>({ status: 'missing', roles });
              }

              return this.api_service.getSurvey(survey_id).pipe(
                map(survey => ({ status: 'edit' as const, roles, survey })),
                catchError(() => of<SurveyEditorPageLoad>({ status: 'error', roles }))
              );
            }),
            catchError(() => of<SurveyEditorPageLoad>({ status: 'error', roles: [] }))
          )
      )
    ),
    { initialValue: null }
  );

  constructor() {
    this.pending_changes_guard.registerBeforeUnload(this, this.destroy_ref);
    this.registerPendingChangesSaveStatusTimeoutCleanupOnDestroy();

    toObservable(this.editor_page_load)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe(load => {
        if (load === null) {
          return;
        }

        this.applyEditorPageLoad(load);
      });
  }

  ngOnInit(): void {
    this.setLoading(true);
    this.applyDefaultSchedule();

    this.form.controls.start_date.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe((): void => {
      this.form.controls.end_date.updateValueAndValidity();
    });

    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe((): void => {
      if (this.loading() || !this.form.dirty) {
        return;
      }

      this.pending_changes_save_status.markPending();
    });
  }

  save(): void {
    this.syncWorkerDepartmentCategoriesValidator();
    this.form.markAllAsTouched();
    this.refreshRolesError();
    this.refreshDepartmentCategoriesError();

    if (this.loading()) {
      return;
    }

    if (this.form.invalid) {
      this.snack_bar.open('Completa los campos obligatorios para guardar.', undefined, { duration: 4000 });

      return;
    }

    const title = this.form.getRawValue().title.trim();

    this.pending_changes_save_status.markSaving();
    this.setLoading(true);

    if (this.is_create_mode()) {
      this.createSurvey(title);

      return;
    }

    this.updateSurvey(title);
  }

  canDeactivate(): boolean {
    return this.loading() || !this.form.dirty;
  }

  onRoleIdsChange(role_ids: number[]): void {
    if (this.loading()) {
      return;
    }

    setTargetedRoleSelection(this.form.controls.role_ids, role_ids);
    this.refreshRolesError();

    if (!this.includesWorkerRole(role_ids)) {
      setTargetedDepartmentCategorySelection(this.form.controls.department_category_ids, []);
    }

    this.syncWorkerDepartmentCategoriesValidator();
    this.propagateSurveyAudienceToUnsetSections(role_ids);
    this.pending_changes_save_status.markPending();
  }

  onDepartmentCategoryIdsChange(department_category_ids: number[]): void {
    if (this.loading()) {
      return;
    }

    setTargetedDepartmentCategorySelection(this.form.controls.department_category_ids, department_category_ids);
    this.refreshDepartmentCategoriesError();
    this.propagateSurveyAudienceToUnsetSections(this.form.controls.role_ids.value);
    this.pending_changes_save_status.markPending();
  }

  openAddSectionDialog(): void {
    if (this.loading()) {
      return;
    }

    this.mat_dialog
      .open(AddSurveySectionDialogComponent, {
        width: '640px',
        maxWidth: '95vw',
      })
      .afterClosed()
      .pipe(
        filter((type): type is SurveyVariableConditionSectionTypeEnum => type !== undefined),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe((type: SurveyVariableConditionSectionTypeEnum): void => {
        this.addSection(type);
      });
  }

  dropSection(event: CdkDragDrop<SurveySectionDraft[]>): void {
    if (this.loading() || event.previousIndex === event.currentIndex) {
      return;
    }

    this.sections.update(sections => {
      const next_sections = [...sections];
      moveItemInArray(next_sections, event.previousIndex, event.currentIndex);

      return next_sections.map((section, index) => ({ ...section, order: index }));
    });
    this.markFormHasPendingChanges();
  }

  removeSection(section: SurveySectionDraft): void {
    if (this.loading()) {
      return;
    }

    if (!confirm(`¿Eliminar la sección "${section.title}"?`)) {
      return;
    }

    if (section.id === null) {
      this.removeSectionFromState(section);
      this.markFormHasPendingChanges();

      return;
    }

    const survey_id = this.survey_id();

    if (survey_id === null) {
      return;
    }

    this.setLoading(true);

    this.api_service.deleteSurveyVariableConditionSection(section.id).subscribe({
      next: () => {
        this.removeSectionFromState(section);
        this.setLoading(false);
      },
      error: () => {
        this.setLoading(false);
      },
    });
  }

  openSectionSettings(section: SurveySectionDraft): void {
    if (this.loading()) {
      return;
    }

    const survey_id = this.survey_id();

    if (survey_id !== null && section.id !== null && !this.form.dirty) {
      this.navigateToSectionSettings(survey_id, section.id);

      return;
    }

    this.saveSurveyAndOpenSectionSettings(section);
  }

  private removeSectionFromState(section_to_remove: SurveySectionDraft): void {
    this.sections.update(sections =>
      sections
        .filter((section: SurveySectionDraft): boolean => {
          if (section_to_remove.id !== null) {
            return section.id !== section_to_remove.id;
          }

          return section !== section_to_remove;
        })
        .map((section, index) => ({ ...section, order: index }))
    );
  }

  private addSection(type: SurveyVariableConditionSectionTypeEnum): void {
    const option = getSurveySectionType(type);
    const role_ids = [...this.form.controls.role_ids.value];
    const department_category_ids = this.includesWorkerRole(role_ids) ? [...this.form.controls.department_category_ids.value] : [];

    this.sections.update((sections: SurveySectionDraft[]): SurveySectionDraft[] => [
      ...sections,
      {
        id: null,
        type,
        title: option.title,
        badge_label: option.badge_label,
        description: option.description,
        order: sections.length,
        questions_count: 0,
        specific_roles_count: 0,
        meta_label: this.getSectionMetaLabel(0, 0),
        role_ids,
        department_category_ids,
      },
    ]);
    this.markFormHasPendingChanges();
  }

  private createSurvey(title: string): void {
    const body = this.buildUpsertSurveyBody(title);

    this.api_service.createSurvey(body).subscribe({
      next: (survey: Survey) => {
        void this.router.navigate(['/employees', 'rrhh', 'surveys', survey.id, 'edit']);
      },
      error: (): void => {
        this.pending_changes_save_status.markError();
        this.setLoading(false);
      },
    });
  }

  private updateSurvey(title: string): void {
    const survey_id = this.survey_id();

    if (!survey_id) {
      this.setLoading(false);

      return;
    }

    const body = this.buildUpsertSurveyBody(title);

    this.api_service.updateSurvey(survey_id, body).subscribe({
      next: (survey: Survey) => {
        this.populateEditorFromSurvey(survey, { mark_saved: true });
        this.setLoading(false);
      },
      error: (): void => {
        this.pending_changes_save_status.markError();
        this.setLoading(false);
      },
    });
  }

  private applyEditorPageLoad(load: SurveyEditorPageLoad): void {
    this.roles.set(load.roles);

    if (load.status === 'create') {
      this.form.patchValue({
        status: SurveyStatusEnum.Draft,
        role_ids: [],
        department_category_ids: [],
      });
      this.syncWorkerDepartmentCategoriesValidator();
      this.refreshRolesError();
      this.form.markAsPristine();
      this.setLoading(false);

      return;
    }

    if (load.status === 'edit') {
      this.populateEditorFromSurvey(load.survey);
      this.setLoading(false);

      return;
    }

    this.setLoading(false);
  }

  private populateEditorFromSurvey(survey: Survey, options: { mark_saved?: boolean } = {}): void {
    this.survey.set(survey);

    const active_condition = getEditableSurveyVariableCondition(survey);

    this.form.patchValue({
      title: survey.title,
      status: survey.status,
      frequency: active_condition?.frequency ?? SurveyVariableConditionFrequencyEnum.Quarterly,
      start_date: this.toMadridDateInputValue(active_condition?.start_date),
      end_date: this.toMadridDateInputValue(active_condition?.end_date),
      postponable_days: active_condition?.postponable_days ?? 0,
      role_ids: getToAnswerRoleIds(active_condition?.survey_variable_condition_filters ?? [], this.roles()),
      department_category_ids: getToAnswerDepartmentCategoryIds(active_condition?.survey_variable_condition_filters ?? []),
    });

    this.applySectionsFromCondition(active_condition?.survey_variable_condition_sections ?? []);
    this.syncWorkerDepartmentCategoriesValidator();
    this.refreshRolesError();
    this.form.markAsPristine();

    if (options.mark_saved === true) {
      this.pending_changes_save_status.markSaved();

      return;
    }

    this.pending_changes_save_status.reset();
  }

  private applySectionsFromCondition(sections: SurveyVariableConditionSection[]): void {
    const sorted_sections = [...sections].sort((left, right) => left.order - right.order);
    const survey = this.survey();
    const survey_filters = survey !== null ? (getEditableSurveyVariableCondition(survey)?.survey_variable_condition_filters ?? []) : [];

    this.sections.set(
      sorted_sections.map(section => {
        const presentation = getSurveySectionType(section.type);
        const questions_count = section.survey_variable_condition_section_questions_count ?? 0;
        const filters = section.survey_variable_condition_filters ?? [];
        const has_section_role_audience = filters.some(
          filter => filter.direction === SurveyVariableConditionFilterDirectionEnum.ToAnswer && (filter.roles?.length ?? 0) > 0
        );
        const audience_filters = has_section_role_audience ? filters : survey_filters;
        const specific_roles_count = filters.reduce((total, filter) => total + (filter.company_product_user_member_roles_count ?? 0), 0);

        return {
          id: section.id,
          type: section.type,
          title: section.title,
          badge_label: presentation.badge_label,
          description: section.description ?? presentation.description,
          order: section.order,
          questions_count,
          specific_roles_count,
          meta_label: this.getSectionMetaLabel(questions_count, specific_roles_count),
          role_ids: getToAnswerRoleIds(audience_filters, this.roles()),
          department_category_ids: getToAnswerDepartmentCategoryIds(audience_filters),
        };
      })
    );
  }

  private applyDefaultSchedule(): void {
    const today = dayjs().tz('Europe/Madrid');

    this.form.patchValue({
      frequency: SurveyVariableConditionFrequencyEnum.Quarterly,
      start_date: today.format('YYYY-MM-DD'),
      end_date: today.add(10, 'year').format('YYYY-MM-DD'),
      postponable_days: 0,
    });
  }

  /**
   * Same rule as SurveyRepository::getEditableVariableCondition:
   * draft → latest; otherwise → current (fallback latest).
   */
  private getEditableCondition(survey: Survey): SurveyVariableCondition | null {
    if (survey.status === SurveyStatusEnum.Draft) {
      return survey.latest_survey_variable_condition ?? survey.current_survey_variable_condition ?? null;
    }

    return survey.current_survey_variable_condition ?? survey.latest_survey_variable_condition ?? null;
  }

  private resolveSurveyFilter(survey: Survey): SurveyFilter | null {
    const filters = this.getEditableCondition(survey)?.survey_variable_condition_filters ?? [];

    return filters.find(filter => filter.direction === SurveyVariableConditionFilterDirectionEnum.ToAnswer) ?? null;
  }

  private resolveRoleIds(filter: SurveyFilter | null): number[] {
    if (!filter) {
      return this.roles().map(role => role.id);
    }

    return filter.roles?.map(role => role.id) ?? [];
  }

  private setLoading(value: boolean): void {
    this.loading.set(value);
    this.syncFormDisabledState();
  }

  private syncFormDisabledState(): void {
    if (this.loading()) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  }

  private buildVariableConditionPayload(): SurveyVariableConditionPayload {
    const values = this.form.getRawValue();

    return {
      frequency: values.frequency,
      start_date: values.start_date,
      end_date: values.end_date,
      postponable_days: Number(values.postponable_days),
    };
  }

  private buildUpsertSurveyBody(title: string): UpsertSurveyBody {
    return {
      title,
      status: this.form.getRawValue().status,
      variable_condition: this.buildVariableConditionPayload(),
      filter: {
        role_ids: this.form.getRawValue().role_ids,
        department_category_ids: this.worker_role_selected() ? this.form.getRawValue().department_category_ids : [],
      },
      sections: this.buildSectionsPayload(),
    };
  }

  private buildSectionsPayload(): SurveyVariableConditionSectionPayload[] {
    const survey_role_ids = [...this.form.getRawValue().role_ids];
    const survey_department_category_ids = this.worker_role_selected() ? [...this.form.getRawValue().department_category_ids] : [];

    return this.sections().map(section => {
      const role_ids = section.role_ids.length > 0 ? section.role_ids : survey_role_ids;
      const department_category_ids = section.role_ids.length > 0 ? section.department_category_ids : survey_department_category_ids;

      return {
        ...(section.id !== null ? { id: section.id } : {}),
        title: section.title,
        description: section.description,
        type: section.type,
        order: section.order,
        filter: {
          role_ids,
          department_category_ids,
        },
      };
    });
  }

  private propagateSurveyAudienceToUnsetSections(role_ids: number[]): void {
    const department_category_ids = this.includesWorkerRole(role_ids) ? [...this.form.controls.department_category_ids.value] : [];

    this.sections.update(sections =>
      sections.map(section =>
        section.role_ids.length === 0
          ? {
              ...section,
              role_ids: [...role_ids],
              department_category_ids,
            }
          : section
      )
    );
  }

  private markFormHasPendingChanges(): void {
    this.form.markAsDirty();
    this.pending_changes_save_status.markPending();
  }

  private saveSurveyAndOpenSectionSettings(section: SurveySectionDraft): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.snack_bar.open('Completa los campos obligatorios para guardar.', undefined, { duration: 4000 });

      return;
    }

    const title = this.form.getRawValue().title.trim();
    const body = this.buildUpsertSurveyBody(title);

    this.pending_changes_save_status.markSaving();
    this.setLoading(true);

    if (this.is_create_mode()) {
      this.api_service.createSurvey(body).subscribe({
        next: (survey: Survey): void => {
          this.openPersistedSectionSettings(survey, section);
        },
        error: (): void => {
          this.pending_changes_save_status.markError();
          this.setLoading(false);
        },
      });

      return;
    }

    const survey_id = this.survey_id();

    if (survey_id === null) {
      this.setLoading(false);

      return;
    }

    this.api_service.updateSurvey(survey_id, body).subscribe({
      next: (survey: Survey): void => {
        this.openPersistedSectionSettings(survey, section);
      },
      error: (): void => {
        this.pending_changes_save_status.markError();
        this.setLoading(false);
      },
    });
  }

  private openPersistedSectionSettings(survey: Survey, source_section: SurveySectionDraft): void {
    const persisted_section = this.findPersistedSection(survey, source_section);

    this.form.markAsPristine();
    this.pending_changes_save_status.markSaved();

    if (persisted_section === null) {
      this.setLoading(false);
      this.snack_bar.open('No se ha podido encontrar la sección guardada.', undefined, { duration: 4000 });
      void this.router.navigate(['/employees', 'rrhh', 'surveys', survey.id, 'edit']);

      return;
    }

    this.navigateToSectionSettings(survey.id, persisted_section.id);
  }

  private findPersistedSection(survey: Survey, source_section: SurveySectionDraft): SurveyVariableConditionSection | null {
    const active_condition = getEditableSurveyVariableCondition(survey);
    const sections = active_condition?.survey_variable_condition_sections ?? [];

    if (source_section.id !== null) {
      const section_by_id = sections.find(section => section.id === source_section.id) ?? null;

      if (section_by_id !== null) {
        return section_by_id;
      }
    }

    return sections.find(section => section.order === source_section.order) ?? null;
  }

  private navigateToSectionSettings(survey_id: number, section_id: number): void {
    void this.router.navigate(['/employees', 'rrhh', 'surveys', survey_id, 'edit', 'sections', section_id, 'edit']);
  }

  private registerPendingChangesSaveStatusTimeoutCleanupOnDestroy(): void {
    this.destroy_ref.onDestroy((): void => {
      this.pending_changes_save_status.destroy();
    });
  }

  private toMadridDateInputValue(value: string | null | undefined): string {
    return value ? dayjs(value).tz('Europe/Madrid').format('YYYY-MM-DD') : '';
  }

  private getSectionMetaLabel(questions_count: number, specific_roles_count: number): string {
    const questions_label = `${questions_count} pregunta${questions_count === 1 ? '' : 's'}`;

    if (specific_roles_count <= 0) {
      return questions_label;
    }

    return `${questions_label} · ${specific_roles_count} roles específicos`;
  }

  private syncWorkerDepartmentCategoriesValidator(): void {
    const categories_control = this.form.controls.department_category_ids;

    categories_control.setValidators(this.includesWorkerRole() ? [minSelectedValidator(1)] : []);
    categories_control.updateValueAndValidity({ emitEvent: false });
    this.refreshDepartmentCategoriesError();
  }

  private refreshRolesError(): void {
    const roles_control = this.form.controls.role_ids;

    this.roles_error.set(roles_control.touched && roles_control.hasError('minSelected') ? 'Selecciona al menos un rol.' : null);
  }

  private refreshDepartmentCategoriesError(): void {
    const categories_control = this.form.controls.department_category_ids;

    this.department_categories_error.set(
      categories_control.touched && categories_control.hasError('minSelected') ? 'Selecciona al menos una categoría de departamento.' : null
    );
  }

  private includesWorkerRole(role_ids: number[] = this.form.controls.role_ids.value): boolean {
    return this.roles().some(role => role.slug === RoleSlugEnum.WORKER && role_ids.includes(role.id));
  }
}

function minSelectedValidator(min: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (!Array.isArray(value) || value.length < min) {
      return { minSelected: { min, actual: Array.isArray(value) ? value.length : 0 } };
    }

    return null;
  };
}

type SurveyEditorPageLoad =
  | { status: 'create'; roles: Role[] }
  | { status: 'edit'; roles: Role[]; survey: Survey }
  | { status: 'missing'; roles: Role[] }
  | { status: 'error'; roles: Role[] };

export function getEditableSurveyVariableCondition(survey: Survey): SurveyVariableCondition | null {
  return survey.status === SurveyStatusEnum.Draft
    ? (survey.latest_survey_variable_condition ?? null)
    : (survey.current_survey_variable_condition ?? survey.latest_survey_variable_condition ?? null);
}
